import type React from 'react';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Navigate,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { AppLayout } from './routes/AppLayout.tsx';
import { PortalLayout } from './routes/PortalLayout.tsx';
import { SettingsIndex, SettingsLayout } from './routes/SettingsLayout.tsx';
import { SignIn } from './routes/SignIn.tsx';
import { NoCompany } from './routes/NoCompany.tsx';
import { AcceptInvite } from './routes/AcceptInvite.tsx';
import { PriceBid } from './routes/PriceBid.tsx';
import { RequireSession } from './lib/require-session.tsx';
import {
  FILE_ROOTS,
  FILE_VIEWS,
  SCHEDULE_VIEWS,
  type FileView,
  type ScheduleView,
} from './routes/route-search.ts';
import type { FolderKind, MemberKind } from '@buildmate/core';

/**
 * Every page but the shell and the three pre-session screens is loaded on
 * demand.
 *
 * Measured, not assumed: one 738 kB chunk was shipped to every page, and
 * the portal dashboard scored 65 for performance against SPEC §9 criterion
 * 10's threshold of 80 (P4-25). A homeowner's dashboard was downloading
 * the Gantt chart, the files browser and the schedule editor to show four
 * panels.
 *
 * `SignIn`, `NoCompany` and `AcceptInvite` stay eager because they are
 * what an arriving browser renders first — splitting them would add a
 * round trip to the only screen whose speed every user sees. `AppLayout`
 * stays eager because it is the frame every internal page draws inside.
 */
const LazySubPortal = lazyRouteComponent(() => import('./routes/SubPortal.tsx'), 'SubPortal');

const LazyClientPortal = lazyRouteComponent(
  () => import('./routes/ClientPortal.tsx'),
  'ClientPortal',
);

const LazyPortalSchedule = lazyRouteComponent(
  () => import('./routes/PortalSchedulePage.tsx'),
  'PortalSchedulePage',
);

const LazyPortalBudget = lazyRouteComponent(
  () => import('./routes/PortalBudgetPage.tsx'),
  'PortalBudgetPage',
);

const LazyPortalInvoices = lazyRouteComponent(
  () => import('./routes/PortalInvoicesPage.tsx'),
  'PortalInvoicesPage',
);

const LazyPortalWarranties = lazyRouteComponent(
  () => import('./routes/PortalWarrantiesPage.tsx'),
  'PortalWarrantiesPage',
);

const LazyPortalSpecifications = lazyRouteComponent(
  () => import('./routes/PortalSpecificationsPage.tsx'),
  'PortalSpecificationsPage',
);

const lazy = <Module extends Record<Key, React.ComponentType>, Key extends string>(
  load: () => Promise<Module>,
  name: Key,
) => lazyRouteComponent(load, name);

/**
 * Route guards read a session snapshot kept on the router context rather than
 * a hook, because beforeLoad runs outside React. SessionProvider pushes into
 * it, so there is still one source of truth for who is signed in.
 */
export interface RouterContext {
  getStatus: () => 'loading' | 'signed-out' | 'no-company' | 'ready';
  /** Null until a company is active. Splits the internal app from the portals. */
  getKind: () => MemberKind | null;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: () => <Outlet /> });

/**
 * Where a member of this kind belongs.
 *
 * One place rather than a ternary repeated in four guards, which is how the
 * sub redirect got written three times and the client one would have been
 * forgotten in at least one of them.
 */
function surfaceFor(kind: MemberKind | null): '/sub' | '/portal' | '/app' {
  if (kind === 'sub') return '/sub';
  if (kind === 'client') return '/portal';
  return '/app';
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: ({ context }) => {
    const status = context.getStatus();
    if (status === 'signed-out') throw redirect({ to: '/signin' });
    if (status === 'no-company') throw redirect({ to: '/no-company' });
    // A sub belongs on their own surface. Landing them in the internal app
    // would show navigation the rules deny anyway, which reads as a broken
    // product rather than as a boundary.
    if (status === 'ready') throw redirect({ to: surfaceFor(context.getKind()) });
  },
  component: () => <p className="p-6 text-sm text-slate-500">Loading…</p>,
});

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signin',
  beforeLoad: ({ context }) => {
    // Someone already signed in has no business on the sign-in form.
    if (context.getStatus() === 'ready') {
      throw redirect({ to: surfaceFor(context.getKind()) });
    }
  },
  component: SignIn,
});

const noCompanyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/no-company',
  component: NoCompany,
});

// Deliberately outside the /app guard: someone following an invite has no
// company yet, so requiring one would lock them out of the thing that gives
// them one.
// Outside every guard, like the invite route and for the same reason:
// a trade pricing a package by emailed link has no account, which is
// the whole point of SCRUM-308.
const priceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/price/$token',
  component: PriceBid,
});

const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite/$token',
  component: AcceptInvite,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app',
  beforeLoad: ({ context }) => {
    const status = context.getStatus();
    if (status === 'signed-out') throw redirect({ to: '/signin' });
    if (status === 'no-company') throw redirect({ to: '/no-company' });
    // Both external kinds have their own surface. Landing either in the
    // internal app would show them navigation the rules deny anyway, which
    // reads as a broken product rather than as a boundary.
    const kind = context.getKind();
    if (kind === 'sub' || kind === 'client') throw redirect({ to: surfaceFor(kind) });
  },
  component: () => (
    <RequireSession>
      <AppLayout />
    </RequireSession>
  ),
});

/**
 * The subcontractor portal.
 *
 * Its own top-level route rather than a child of /app, because it is not the
 * internal app with things hidden — it has a different layout, a different
 * audience and a different job. Internal staff are sent back: a PM wanting
 * to see what a sub sees needs a preview, not a route that lies about who is
 * looking.
 */
const subRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sub',
  beforeLoad: ({ context }) => {
    const status = context.getStatus();
    if (status === 'signed-out') throw redirect({ to: '/signin' });
    if (status === 'no-company') throw redirect({ to: '/no-company' });
    if (status === 'ready' && context.getKind() !== 'sub') throw redirect({ to: '/app' });
  },
  component: () => (
    <RequireSession>
      <LazySubPortal />
    </RequireSession>
  ),
});

/**
 * The homeowner's portal.
 *
 * Its own top-level route for the same reasons as `/sub`: a different
 * layout, a different audience and a different job. Internal staff are sent
 * back — somebody wanting to see what an owner sees needs a preview, not a
 * route that lies about who is looking.
 */
const portalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal',
  beforeLoad: ({ context }) => {
    const status = context.getStatus();
    if (status === 'signed-out') throw redirect({ to: '/signin' });
    if (status === 'no-company') throw redirect({ to: '/no-company' });
    if (status === 'ready' && context.getKind() !== 'client') {
      throw redirect({ to: surfaceFor(context.getKind()) });
    }
  },
  // A layout rather than a page, since P4-21 gave the schedule an address
  // of its own. `PortalLayout` is eager for the same reason `AppLayout` is:
  // it is the frame every portal page draws inside, so splitting it would
  // add a round trip before anything appears.
  component: () => (
    <RequireSession>
      <PortalLayout />
    </RequireSession>
  ),
});

/**
 * The portal dashboard.
 *
 * `job` is in the URL here for the same reason it is on every other
 * portal page, and SCRUM-297 is what happens when it is not: the
 * dashboard held the choice in React state, so a client with two
 * projects picked one, moved to Schedule or Invoices, and was silently
 * put back on whichever job sorted first. Nothing said it had
 * happened, so the figures they were reading were for another project.
 */
const portalIndexRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/',
  validateSearch: (search: Record<string, unknown>): { job?: string } => ({
    job: typeof search.job === 'string' && search.job.length > 0 ? search.job : undefined,
  }),
  component: () => <LazyClientPortal />,
});

/**
 * The owner's schedule, read-only.
 *
 * `view` and `job` live in the URL rather than in state because both are
 * things somebody sends: "here is the Gantt for our job" has to survive
 * being pasted into a message. `job` is optional — an owner with one
 * project never needs it, and the page falls back to the same first-by-name
 * rule the dashboard uses.
 */
const portalScheduleRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: 'schedule',
  validateSearch: (search: Record<string, unknown>): { view: ScheduleView; job?: string } => ({
    view: SCHEDULE_VIEWS.includes(search.view as ScheduleView)
      ? (search.view as ScheduleView)
      : 'list',
    job: typeof search.job === 'string' && search.job.length > 0 ? search.job : undefined,
  }),
  component: () => <LazyPortalSchedule />,
});

/**
 * The owner's budget, read-only (P5-08).
 *
 * `job` is in the URL for the same reason the schedule's is — an owner with
 * two projects sends a link to one of them — and there is no `view`,
 * because there is one view. Nothing else on this route is a parameter: the
 * figures are what they are.
 */
const portalBudgetRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: 'budget',
  validateSearch: (search: Record<string, unknown>): { job?: string } => ({
    job: typeof search.job === 'string' && search.job.length > 0 ? search.job : undefined,
  }),
  component: () => <LazyPortalBudget />,
});

/**
 * The owner's invoices (P5-12c).
 *
 * `job` in the URL like the other two portal pages, because an owner with
 * two projects sends a link to one of them.
 */
const portalInvoicesRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: 'invoices',
  validateSearch: (search: Record<string, unknown>): { job?: string } => ({
    job: typeof search.job === 'string' && search.job.length > 0 ? search.job : undefined,
  }),
  component: () => <LazyPortalInvoices />,
});

/**
 * Where the owner reports a defect (P8-03).
 *
 * `job` like every other portal page, and for a reason that bites harder
 * here than elsewhere: warranty work outlives a build, so somebody with
 * two finished houses is the normal case rather than the exception.
 */
const portalWarrantiesRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: 'warranties',
  validateSearch: (search: Record<string, unknown>): { job?: string } => ({
    job: typeof search.job === 'string' && search.job.length > 0 ? search.job : undefined,
  }),
  component: () => <LazyPortalWarranties />,
});

/**
 * The owner's specification sheet (P8-06b).
 *
 * Printable, which `docs/features/13-later-modules.md` asks for by name:
 * a spec sheet is carried round a half-built house and argued over.
 */
const portalSpecificationsRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: 'specifications',
  validateSearch: (search: Record<string, unknown>): { job?: string } => ({
    job: typeof search.job === 'string' && search.job.length > 0 ? search.job : undefined,
  }),
  component: () => <LazyPortalSpecifications />,
});

const jobsIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: lazy(() => import('./routes/JobsPage.tsx'), 'JobsPage'),
});

/**
 * Company settings, as a layout with its pages beneath (P5-22).
 *
 * `costCodesRoute` carried a note from the day it was written saying it
 * should become a child of a settings layout "when the second arrives".
 * There are now four, reached from two different menus, so the note came
 * due — and folding them under one entry makes the main navigation
 * **shorter**, which is worth having on its own: a two-line header has
 * pushed the Gantt chart below the fold three times.
 *
 * `SettingsLayout` is eager like `AppLayout` and `PortalLayout`: it is the
 * frame every settings page draws inside, so splitting it would add a
 * round trip before anything appeared.
 */
const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'settings',
  component: () => <SettingsLayout />,
});

const costCodesRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'cost-codes',
  component: lazy(() => import('./routes/CostCodesPage.tsx'), 'CostCodesPage'),
});

/**
 * Inventory, out of Settings and into the main navigation (SCRUM-263).
 *
 * Stock and the catalog were settings pages, each with a defensible
 * reason at the time: this is the catalog grown stock rather than a new
 * area of the product, and the main navigation has been one word away
 * from wrapping since Phase 5. Neither reason survives the maintainer
 * not being able to find the module — the same finding that moved
 * equipment out on 2026-10-21.
 *
 * **One page, no tabs** since SCRUM-264: the two screens became one
 * list, so there is nothing to switch between. The `view` search
 * parameter the tab strip used is gone with it, and the redirects below
 * no longer carry one — an old link with `?view=catalog` still lands on
 * the list, which is where its catalog now is.
 */
const inventoryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'inventory',
  component: lazy(() => import('./routes/InventoryPage.tsx'), 'InventoryPage'),
});

/**
 * What is out, and with whom (SCRUM-270).
 *
 * Its own route rather than a section of the list, because it is a page
 * somebody sends to a foreman — and because it answers a different
 * question: the list is "what do we have", this is "what is gone and
 * who has it".
 */
const stockOutRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'inventory/out',
  component: lazy(() => import('./routes/StockOutPage.tsx'), 'StockOutPage'),
});

/**
 * The old settings URLs, **redirecting rather than gone**.
 *
 * Dev has been in use for weeks and these are in somebody's bookmarks
 * and in this repository's own test suites. A dead link is a worse
 * answer than a redirect for a page that still exists one directory
 * away, and a redirect costs two lines.
 *
 * `Navigate` rather than a `beforeLoad` redirect, for the reason
 * `settingsIndexRoute` gives: it keeps the whole decision in the
 * component tree, where the session is in hand.
 */
const stockRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'stock',
  component: () => <Navigate to="/app/inventory" replace />,
});

const catalogRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'catalog',
  component: () => <Navigate to="/app/inventory" replace />,
});

const taxRatesRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'tax-rates',
  component: lazy(() => import('./routes/TaxRatesPage.tsx'), 'TaxRatesPage'),
});

/**
 * `/app/settings` with nothing after it.
 *
 * Sends somebody to the first page their role allows rather than showing a
 * shell with an empty body. `Navigate` rather than a redirect in
 * `beforeLoad`, because which page is first depends on the session and
 * `beforeLoad` runs before the permissions are in hand.
 */
const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/',
  component: () => <SettingsIndex />,
});

/**
 * Safety notices (SCRUM-208).
 *
 * The fourth settings page, which makes the note on `costCodesRoute`
 * overdue: a settings shell is now worth having. Raised rather than built
 * here, because the shell is its own change and this page works without
 * one.
 */
const safetyNoticesRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'safety-notices',
  component: lazy(() => import('./routes/SafetyNoticesPage.tsx'), 'SafetyNoticesPage'),
});

/**
 * The equipment register, and where everything is (SCRUM-209).
 *
 * A settings page because it is a company-level list like the chart of
 * accounts: one register, worked across every job. Booking a machine onto
 * a schedule item is a separate screen on the schedule itself.
 *
 * `view` is in the URL rather than in component state, because "where is
 * everything next week" is a page somebody sends to a foreman. It is a
 * search parameter rather than a seventh settings tab for the reason
 * `SettingsLayout` gives at length: the navigation has been one word from
 * wrapping since Phase 5 began, and these two views answer questions about
 * the same list.
 */
const equipmentRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'equipment',
  validateSearch: (search: Record<string, unknown>): { view: 'register' | 'where' | 'types' } => ({
    view: search.view === 'where' ? 'where' : search.view === 'types' ? 'types' : 'register',
  }),
  component: lazy(() => import('./routes/EquipmentPage.tsx'), 'EquipmentPage'),
});

/**
 * Warranty claims on a finished job (SCRUM-140, P8-02).
 *
 * A job tab, because a claim belongs to the job it came from — the path
 * is `companies/{c}/jobs/{j}/warrantyClaims` for the same reason to-dos
 * and daily logs are job-scoped: a `list` rule can enforce job assignment
 * with one read.
 */
const warrantiesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'jobs/$jobId/warranties',
  component: lazy(() => import('./routes/WarrantiesPage.tsx'), 'WarrantiesPage'),
});

/**
 * The job's specification sheet (SCRUM-141, P8-06).
 *
 * A job tab for the same reason warranties is one: a sheet describes one
 * building, and the path `companies/{c}/jobs/{j}/specSections` lets a
 * `list` rule enforce job assignment with a single read.
 */
/**
 * The crew's own time clock (SCRUM-142, P8-07).
 *
 * Not under a job, because a shift names one rather than belonging to a
 * page about one — and a crew member who has to find the job first is a
 * crew member who clocks in late.
 */
/**
 * Requests for information (SCRUM-144, P8-11b).
 *
 * A job tab, because a question is about one building and the path is
 * what lets a `list` rule enforce job assignment with a single read.
 */
/**
 * Bid packages (SCRUM-143, P8-10b).
 *
 * A job tab: a package prices one job's work, and the path is what lets a
 * `list` rule enforce job assignment with a single read.
 */
const bidsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'jobs/$jobId/bids',
  component: lazy(() => import('./routes/BidsPage.tsx'), 'BidsPage'),
});

const rfisRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'jobs/$jobId/rfis',
  component: lazy(() => import('./routes/RfisPage.tsx'), 'RfisPage'),
});

const timeClockRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'time',
  component: lazy(() => import('./routes/TimeClockPage.tsx'), 'TimeClockPage'),
});

/**
 * Portfolio reports (P8-12).
 *
 * Company-level rather than a job tab, which is the point of them: every
 * other screen answers a question about one job. Gated inside the page
 * on `reports: view`.
 */
const reportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'reports',
  component: lazy(() => import('./routes/ReportsPage.tsx'), 'ReportsPage'),
});

const specificationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'jobs/$jobId/specifications',
  component: lazy(() => import('./routes/SpecificationsPage.tsx'), 'SpecificationsPage'),
});

/**
 * The checklist template library (P3-09e).
 *
 * A settings page because a template is company reference data, like the
 * chart of accounts: one library, applied across every job. Gated inside
 * the page on `todos: view` to read and `todos: admin` to change.
 */
const checklistTemplatesRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'checklists',
  component: lazy(() => import('./routes/ChecklistTemplatesPage.tsx'), 'ChecklistTemplatesPage'),
});

/**
 * What a job needs on site (SCRUM-210).
 *
 * Its own route rather than a panel on the schedule, because it is a list
 * somebody works from with a phone in one hand — and because a link to it
 * has to be sendable.
 */
const materialsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'jobs/$jobId/materials',
  component: lazy(() => import('./routes/MaterialsPage.tsx'), 'MaterialsPage'),
});

/**
 * The assistant's settings (SCRUM-223): which model, and what it has cost.
 */
const assistantSettingsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'assistant',
  component: lazy(() => import('./routes/AssistantSettingsPage.tsx'), 'AssistantSettingsPage'),
});

const membersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'people',
  component: lazy(() => import('./routes/MembersPage.tsx'), 'MembersPage'),
});

const directoryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'directory',
  component: lazy(() => import('./routes/DirectoryPage.tsx'), 'DirectoryPage'),
});

const jobRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'jobs/$jobId',
  component: lazy(() => import('./routes/JobSummary.tsx'), 'JobSummary'),
});

// A sibling of the job summary rather than a child, because the job page is
// not a layout yet. JobScheduleTabs links the schedule pages to each other.
const jobWorkdaysRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'jobs/$jobId/workdays',
  component: lazy(() => import('./routes/WorkdayExceptionsPage.tsx'), 'WorkdayExceptionsPage'),
});

const jobScheduleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'jobs/$jobId/schedule',
  // The chosen view lives in the URL so a link to a job's calendar can be
  // sent to someone and the back button moves between views.
  validateSearch: (search: Record<string, unknown>): { view: ScheduleView } => ({
    view: SCHEDULE_VIEWS.includes(search.view as ScheduleView)
      ? (search.view as ScheduleView)
      : 'list',
  }),
  component: lazy(() => import('./routes/SchedulePage.tsx'), 'SchedulePage'),
});

/**
 * A job's files.
 *
 * Root, folder and view all live in the URL. A photograph somebody needs to
 * look at is a link you send, and a folder three levels down that only
 * exists as component state is not sendable.
 */
const jobFilesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'jobs/$jobId/files',
  validateSearch: (
    search: Record<string, unknown>,
  ): { root: FolderKind; view: FileView; folder?: string } => ({
    root: FILE_ROOTS.includes(search.root as FolderKind)
      ? (search.root as FolderKind)
      : 'documents',
    view: FILE_VIEWS.includes(search.view as FileView) ? (search.view as FileView) : 'list',
    folder:
      typeof search.folder === 'string' && search.folder.length > 0 ? search.folder : undefined,
  }),
  component: lazy(() => import('./routes/FilesPage.tsx'), 'FilesPage'),
});

const jobDailyLogsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'jobs/$jobId/daily-logs',
  component: lazy(() => import('./routes/DailyLogsPage.tsx'), 'DailyLogsPage'),
});

const jobTodosRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'jobs/$jobId/todos',
  component: lazy(() => import('./routes/TodosPage.tsx'), 'TodosPage'),
});

const jobChangeOrdersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'jobs/$jobId/change-orders',
  component: lazy(() => import('./routes/ChangeOrdersPage.tsx'), 'ChangeOrdersPage'),
});

const jobSelectionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'jobs/$jobId/selections',
  component: lazy(() => import('./routes/SelectionsPage.tsx'), 'SelectionsPage'),
});

const jobEstimateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'jobs/$jobId/estimate',
  component: lazy(() => import('./routes/EstimatePage.tsx'), 'EstimatePage'),
});

const jobBudgetRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'jobs/$jobId/budget',
  component: lazy(() => import('./routes/BudgetPage.tsx'), 'BudgetPage'),
});

const jobPurchaseOrdersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'jobs/$jobId/purchase-orders',
  component: lazy(() => import('./routes/PurchaseOrdersPage.tsx'), 'PurchaseOrdersPage'),
});

const jobBillsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'jobs/$jobId/bills',
  component: lazy(() => import('./routes/BillsPage.tsx'), 'BillsPage'),
});

const jobInvoicesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'jobs/$jobId/invoices',
  component: lazy(() => import('./routes/InvoicesPage.tsx'), 'InvoicesPage'),
});

const jobBaselineRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'jobs/$jobId/baseline',
  component: lazy(() => import('./routes/BaselinePage.tsx'), 'BaselinePage'),
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  signInRoute,
  noCompanyRoute,
  inviteRoute,
  priceRoute,
  subRoute,
  portalRoute.addChildren([
    portalIndexRoute,
    portalScheduleRoute,
    portalBudgetRoute,
    portalInvoicesRoute,
    portalWarrantiesRoute,
    portalSpecificationsRoute,
  ]),
  appRoute.addChildren([
    jobsIndexRoute,
    jobRoute,
    jobScheduleRoute,
    materialsRoute,
    warrantiesRoute,
    specificationsRoute,
    timeClockRoute,
    reportsRoute,
    rfisRoute,
    bidsRoute,
    jobFilesRoute,
    jobDailyLogsRoute,
    jobTodosRoute,
    jobChangeOrdersRoute,
    jobSelectionsRoute,
    jobEstimateRoute,
    jobBudgetRoute,
    jobPurchaseOrdersRoute,
    jobBillsRoute,
    jobInvoicesRoute,
    jobBaselineRoute,
    jobWorkdaysRoute,
    membersRoute,
    directoryRoute,
    inventoryRoute,
    stockOutRoute,
    settingsRoute.addChildren([
      settingsIndexRoute,
      costCodesRoute,
      catalogRoute,
      stockRoute,
      taxRatesRoute,
      safetyNoticesRoute,
      equipmentRoute,
      checklistTemplatesRoute,
      assistantSettingsRoute,
    ]),
  ]),
]);

export function buildRouter(context: RouterContext) {
  return createRouter({
    routeTree,
    context,
    defaultPreload: 'intent',
    defaultNotFoundComponent: () => (
      <div className="p-6">
        <h1 className="text-lg font-semibold text-slate-900">Page not found</h1>
        <p className="mt-1 text-sm text-slate-600">That address does not match anything here.</p>
      </div>
    ),
  });
}

export type AppRouter = ReturnType<typeof buildRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}
